import { Box, Button, CircularProgress, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { accessToken } from "../../atoms/atom";
import { useAlert } from "../../componentes/AlertProvider";
import { FormService } from "../../services/FormService";
import { FormDTO } from "../../types/FormDTO";
import { formQuestions, QuestionDTO } from "../../types/QuestionDTO";
import './Question.scss';

const Question: FC = () => {
	const { id } = useParams<{ id: string }>();
	const [answersMap, setAnswersMap] = useState<Map<string, string>>(new Map());
	const [form, setForm] = useState<FormDTO>();
	const progress = form && form.currentQuestion
		? ((form.currentQuestion) * 100 / formQuestions.length).toFixed(0) : 0;
	const [accessTokenAtom] = useAtom(accessToken);
	const isMounted = useRef(true);
	const [currentQuestion, setCurrentQuestion] = useState<QuestionDTO>();
	const [loading, setLoading] = useState(false);
	const questions = formQuestions;
	const service = new FormService();
	const { showError } = useAlert();
	const navigate = useNavigate();
	const boldPart = currentQuestion?.header && currentQuestion?.header.includes("últimas 4 semanas")
		? "últimas 4 semanas"
		: currentQuestion?.header && currentQuestion?.header.includes("últimos 12 meses")
			? "últimos 12 meses"
			: '';
	const [before, after] = currentQuestion?.header && boldPart
		? currentQuestion.header.split(boldPart)
		: ["", ""];

	const headerClass = currentQuestion && currentQuestion.header ? "" : "hidden";
	const alterntiveClass = currentQuestion && currentQuestion.alternatives ? "" : "hidden";
	const textClass = currentQuestion && currentQuestion.text ? "" : "hidden";

	const nextQuestion = async () => {
		sendAnswer();
	}

	const previousQuestion = async () => {
		const newForm = {
			currentQuestion: (form?.currentQuestion || 2) - 1
		} as FormDTO;
		setForm(newForm);
	}

	const handleRadioChange = (value: string) => {
		const newMap = new Map(answersMap);
		newMap.set(currentQuestion!.id.toString(), value);
		setAnswersMap(newMap);
	};

	const handleTextChange = (value: string) => {
		const newMap = new Map(answersMap);
		newMap.set(currentQuestion!.id.toString(), value);
		setAnswersMap(newMap);
	};

	useEffect(() => {
		if (form?.currentQuestion === 91) {
			navigate(`/${id}/fim`);
			return;
		}
		setCurrentQuestion(questions.find(q => q.id === form?.currentQuestion));
	}, [form?.currentQuestion]);

	useEffect(() => {
		const token = accessTokenAtom || localStorage.getItem("access-token");
		if (!token && isMounted.current) {
			navigate(`/${id}/login`);
		}
	}, [accessTokenAtom]);

	useEffect(() => {
		if (!id) return;
		const load = async () => {
			const response = await service.getForm(id);
			if (response && response.data) {
				setForm(response.data);
			}
		};
		load();
	}, [id])

	return (
		currentQuestion
		? <div className="question">
			<div className="question-body">
				<Box sx={{ position: 'relative', display: 'inline-flex' }}>
					<CircularProgress
						sx={{width: "60px !important", height: "60px !important"}}
						variant="determinate"
						value={Number(progress)}
					/>
					<Box
						sx={{
							top: 0,
							left: 0,
							bottom: 0,
							right: 0,
							position: 'absolute',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Typography
							variant="caption"
							component="div"
							sx={{ color: 'text.secondary', fontSize: "2.4vh !important" }}
	
						>
							{`${progress}%`}
						</Typography>
					</Box>
				</Box>
				<div className={`question-header ${headerClass}`}>
					<Typography variant={boldPart ? 'h6' : 'h5'}>
						{before}
						<b><u>{boldPart}</u></b>
						{after}
					</Typography>
				</div>
				<div className="question-title">
					<Typography variant="h6">
						{currentQuestion.title}
					</Typography>
				</div>
				<div className={`question-alternatives ${alterntiveClass}`}>
					<FormControl id="question-form-control">
						<RadioGroup
							aria-labelledby="demo-radio-buttons-group-label"
							name="radio-buttons-group"
							value={answersMap.get(currentQuestion?.id.toString() || "") || ""}
						>
							{currentQuestion.alternatives?.map((alt, index) => (
								<FormControlLabel
									key={index}
									value={alt.value? alt.value : alt.name}
									control={<Radio/>}
									label={alt.name}
									onChange={() => handleRadioChange(alt.value ? alt.value : alt.name)}
								/>
							))}
						</RadioGroup>
					</FormControl>
				</div>
				<div className={`question-text ${textClass}`}>
					<TextField
						id="outlined-multiline-static"
						label="Escreva sua resposta"
						multiline
						rows={4}
						fullWidth
						value={answersMap.get(currentQuestion?.id.toString() || "")}
						inputProps={{ maxLength: 255 }}
						onChange={(e) => handleTextChange(e.target.value)}
					/>
				</div>
			</div>
			<Box className="question-footer">
				<Button
					color="primary"
					variant="outlined"
					size="large"
					onClick={() => previousQuestion()}
					disabled={currentQuestion?.id === 1}
				>
					Voltar
				</Button>
				<Button
					variant="contained"
					color="primary"
					size="large"
					onClick={() => nextQuestion()}
					sx={{ width: "105px" }}
					disabled={!answersMap.get(currentQuestion?.id.toString() || "") && !!currentQuestion.alternatives}
				>
					{
						loading
							? <CircularProgress sx={{color: "#48a7dbff"}} size={'25px'}/>
							: currentQuestion?.id === 90 ? "Finalizar" : "Próxima"
					}
				</Button>
			</Box>
		</div>
		: <CircularProgress/>
	);

	async function sendAnswer() {
		setLoading(true);
		try {
			const response = await service
				.sendAnswer(id, currentQuestion?.id, answersMap.get(currentQuestion?.id.toString() || "") || "");
			if (response?.status !== 200) {
				showError();
				return;
			}
			if (form?.currentQuestion === 11) {
				navigate(`/${id}/orientacoes`);
				return;
			}
			if (form?.currentQuestion === 61) {
				navigate(`/${id}/orientacoes2`);
				return;
			}
			if (form?.currentQuestion === 87) {
				navigate(`/${id}/orientacoes3`);
				return;
			}
			if (form?.currentQuestion === 90) {
				navigate(`/${id}/fim`);
				localStorage.removeItem('access-token');
				return;
			}
			if (response?.data?.currentQuestion) {
				const nextQuestionId = response.data.currentQuestion;
				if (!answersMap.has(nextQuestionId.toString())) {
					const newMap = new Map(answersMap);
					newMap.set(nextQuestionId.toString(), "");
					setAnswersMap(newMap);
				}
			}
			setForm(response?.data);
		} catch (error) {
			showError();
		} finally {
			setLoading(false);
		}
	}
}

export default Question